// =========================================================
// About.jsx
// ORION BEACH HOTEL · GUAJIRA
// HISTORIA DEL HOTEL · PREMIUM EDITORIAL
// =========================================================

import { useEffect, useState } from "react";

import {
  ArrowUpRight,
  MapPin,
  MessageCircle,
  Waves,
  Wind,
} from "lucide-react";

import "../styles/about.css";


// =========================================================
// API
// =========================================================

const API =
  import.meta.env.VITE_API_URL ||
  "https://hotelorionmayapo.com/";


// =========================================================
// CONFIG
// =========================================================

const DEFAULT_PHONE =
  "573104794943";


// =========================================================
// DEFAULT IMAGE
// =========================================================

const DEFAULT_IMAGE =
  "/images/cabanas/cabana-1.png";


// =========================================================
// DEFAULT CONTENT
// =========================================================

const DEFAULT_ABOUT = {

  badge:
    "Nuestra historia",

  title:
    "Un lugar nacido frente al mar",

  subtitle:
    "Orión Beach Hotel",

  text1:
    "Orión Beach Hotel nace como un espacio pensado para quienes buscan descubrir La Guajira desde una experiencia cercana al mar, la naturaleza y la tranquilidad.",

  text2:
    "Nuestro propósito es crear un lugar donde cada visitante pueda desconectarse del ritmo cotidiano, disfrutar del Caribe y sentirse conectado con la esencia de este territorio.",

  text3:
    "Hoy Orión es un punto de encuentro para viajeros, familias y amantes del kitesurf que quieren vivir La Guajira de una manera auténtica, relajada y especial.",

  stat1:
    "La Guajira",

  stat1Label:
    "Nuestro hogar",

  stat2:
    "Caribe",

  stat2Label:
    "Frente al mar",

  stat3:
    "Kite House",

  stat3Label:
    "Nuestra esencia",

  button:
    "Conoce nuestras cabañas",

  image:
    DEFAULT_IMAGE,

  imageAlt:
    "Cabaña de Orión Beach Hotel frente al mar en La Guajira",

};


// =========================================================
// NORMALIZE IMAGE
// =========================================================

const normalizeImageUrl = (url) => {

  if (!url) {
    return "";
  }

  const value =
    String(url).trim();

  if (!value) {
    return "";
  }

  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }

  if (value.startsWith("/")) {
    return value;
  }

  return `${API}/${value}`;

};


// =========================================================
// GET ABOUT IMAGE
// =========================================================

const getAboutImage = (data) => {

  if (!data) {
    return "";
  }

  const directImage =
    data.image ||
    data.image_url ||
    data.imageUrl ||
    data.photo ||
    data.photo_url ||
    data.cover;

  if (directImage) {
    return normalizeImageUrl(directImage);
  }

  if (
    Array.isArray(data.images) &&
    data.images.length
  ) {

    const preferred =
      data.images.find(
        (img) =>
          img?.type === "history" ||
          img?.type === "about" ||
          img?.type === "hotel" ||
          img?.type === "profile"
      );

    const image =
      preferred ||
      data.images[0];

    if (typeof image === "string") {
      return normalizeImageUrl(image);
    }

    if (
      image &&
      typeof image === "object"
    ) {
      return normalizeImageUrl(
        image.url ||
        image.image ||
        image.src ||
        image.image_url
      );
    }

  }

  return "";

};


// =========================================================
// NORMALIZE CONTENT
// =========================================================

const normalizeAbout = (data) => {

  if (!data) {
    return DEFAULT_ABOUT;
  }

  const content =
    data.content || data;

  return {

    badge:
      content.badge ||
      DEFAULT_ABOUT.badge,

    title:
      content.title ||
      DEFAULT_ABOUT.title,

    subtitle:
      content.subtitle ||
      DEFAULT_ABOUT.subtitle,

    text1:
      content.text1 ||
      content.history1 ||
      content.description ||
      DEFAULT_ABOUT.text1,

    text2:
      content.text2 ||
      content.history2 ||
      DEFAULT_ABOUT.text2,

    text3:
      content.text3 ||
      content.history3 ||
      DEFAULT_ABOUT.text3,

    stat1:
      content.stat1 ||
      DEFAULT_ABOUT.stat1,

    stat1Label:
      content.stat1Label ||
      DEFAULT_ABOUT.stat1Label,

    stat2:
      content.stat2 ||
      DEFAULT_ABOUT.stat2,

    stat2Label:
      content.stat2Label ||
      DEFAULT_ABOUT.stat2Label,

    stat3:
      content.stat3 ||
      DEFAULT_ABOUT.stat3,

    stat3Label:
      content.stat3Label ||
      DEFAULT_ABOUT.stat3Label,

    button:
      content.button ||
      DEFAULT_ABOUT.button,

    whatsapp:
      content.whatsapp ||
      `https://wa.me/${DEFAULT_PHONE}?text=${encodeURIComponent(
        "Hola, quiero conocer más sobre Orión Beach Hotel y sus cabañas."
      )}`,

    image:
      getAboutImage(data) ||
      DEFAULT_ABOUT.image,

    imageAlt:
      content.imageAlt ||
      DEFAULT_ABOUT.imageAlt,

  };

};


// =========================================================
// COMPONENT
// =========================================================

export default function About() {

  const [about, setAbout] =
    useState(DEFAULT_ABOUT);

  const [loading, setLoading] =
    useState(true);


  // =======================================================
  // LOAD API
  // =======================================================

  useEffect(() => {

    let mounted = true;

    const loadAbout = async () => {

      try {

        const response =
          await fetch(
            `${API}/api/content/about`
          );

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}`
          );
        }

        const data =
          await response.json();

        if (mounted) {
          setAbout(
            normalizeAbout(data)
          );
        }

      } catch (error) {

        console.warn(
          "No se pudo cargar About desde la API. Se utilizará contenido local.",
          error
        );

        if (mounted) {
          setAbout(
            DEFAULT_ABOUT
          );
        }

      } finally {

        if (mounted) {
          setLoading(false);
        }

      }

    };

    loadAbout();

    return () => {
      mounted = false;
    };

  }, []);


  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {

    return (

      <section
        id="historia"
        className="about about--loading"
      >

        <div className="about__loading">

          <span />

          <p>
            Descubriendo nuestra historia...
          </p>

        </div>

      </section>

    );

  }


  // =========================================================
  // RENDER
  // =========================================================

  return (

    <section
      id="historia"
      className="about"
      aria-labelledby="about-title"
    >

      {/* ===================================================
          DECORATIVE BACKGROUND
      =================================================== */}

      <div
        className="about__orb about__orb--one"
        aria-hidden="true"
      />

      <div
        className="about__orb about__orb--two"
        aria-hidden="true"
      />


      {/* ===================================================
          TOP LABEL
      =================================================== */}

      <div className="about__topline">

        <span>
          ORION BEACH HOTEL
        </span>

        <span className="about__topline-line" />

        <span>
          MAYAPO · LA GUAJIRA
        </span>

      </div>


      {/* ===================================================
          MAIN
      =================================================== */}

      <div className="about__container">


        {/* =================================================
            VISUAL
        ================================================= */}

        <div className="about__visual">

          <div className="about__image-frame">

            <div className="about__image">

              <img
                src={
                  about.image ||
                  DEFAULT_IMAGE
                }
                alt={
                  about.imageAlt ||
                  DEFAULT_ABOUT.imageAlt
                }
                loading="lazy"
                onError={(event) => {

                  const fallback =
                    window.location.origin +
                    DEFAULT_IMAGE;

                  if (
                    event.currentTarget.src !==
                    fallback
                  ) {

                    event.currentTarget.src =
                      DEFAULT_IMAGE;

                  }

                }}
              />

              <div
                className="about__image-gradient"
                aria-hidden="true"
              />

              {/* LOCATION */}

              <div className="about__location">

                <div className="about__location-icon">

                  <MapPin size={16} />

                </div>

                <div>

                  <strong>
                    ORIÓN BEACH HOTEL
                  </strong>

                  <span>
                    La Guajira · Colombia
                  </span>

                </div>

              </div>


              {/* IMAGE BADGE */}

              <div className="about__image-badge">

                <Waves size={14} />

                <span>
                  FRENTE AL CARIBE
                </span>

              </div>

            </div>

          </div>


          {/* FLOATING NUMBER */}

          <div
            className="about__number"
            aria-hidden="true"
          >
            <span>
              01
            </span>

            <small>
              HISTORIA
            </small>

          </div>


          {/* WIND MARK */}

          <div
            className="about__wind"
            aria-hidden="true"
          >

            <Wind size={17} />

          </div>

        </div>


        {/* =================================================
            CONTENT
        ================================================= */}

        <div className="about__content">


          {/* EYEBROW */}

          <div className="about__eyebrow">

            <span />

            <span>
              {about.badge}
            </span>

          </div>


          {/* TITLE */}

          <h2
            id="about-title"
            className="about__title"
          >

            {about.title}

          </h2>


          {/* SUBTITLE */}

          <p className="about__subtitle">

            {about.subtitle}

          </p>


          {/* DIVIDER */}

          <div className="about__divider">

            <span />

            <Waves size={14} />

            <span />

          </div>


          {/* HISTORY */}

          <div className="about__history">

            <p className="about__text about__text--lead">
              {about.text1}
            </p>

            <p className="about__text">
              {about.text2}
            </p>

            <p className="about__text">
              {about.text3}
            </p>

          </div>


          {/* STATS */}

          <div className="about__stats">

            <div className="about__stat">

              <strong>
                {about.stat1}
              </strong>

              <span>
                {about.stat1Label}
              </span>

            </div>

            <div className="about__stat">

              <strong>
                {about.stat2}
              </strong>

              <span>
                {about.stat2Label}
              </span>

            </div>

            <div className="about__stat">

              <strong>
                {about.stat3}
              </strong>

              <span>
                {about.stat3Label}
              </span>

            </div>

          </div>


          {/* ACTIONS */}

          <div className="about__actions">

            <a
              href="#habitaciones"
              className="about__button"
            >

              <span>
                {about.button}
              </span>

              <ArrowUpRight size={17} />

            </a>


            <a
              href={
                about.whatsapp ||
                DEFAULT_ABOUT.whatsapp
              }
              className="about__secondary-button"
              target="_blank"
              rel="noopener noreferrer"
            >

              <MessageCircle size={17} />

              <span>
                Hablar con nosotros
              </span>

            </a>

          </div>


          {/* SIGNATURE */}

          <div className="about__signature">

            <span className="about__signature-line" />

            <span>
              MAR · VIENTO · NATURALEZA
            </span>

          </div>

        </div>

      </div>


      {/* ===================================================
          BOTTOM MARQUEE
      =================================================== */}

      <div className="about__bottom">

        <span>
          DESCANSO
        </span>

        <i />

        <span>
          NATURALEZA
        </span>

        <i />

        <span>
          CARIBE
        </span>

        <i />

        <span>
          KITESURF
        </span>

        <i />

        <span>
          LA GUAJIRA
        </span>

      </div>

    </section>

  );

}